import React, { useState, useEffect, useRef } from 'react';
import {
    FiX,
    FiSend,
    FiEyeOff,
    FiEye,
    FiCamera,
    FiArrowUp,
    FiHelpCircle,
    FiChevronRight,
    FiUploadCloud
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import {
    BUSINESS_WHATSAPP_CHAT_URL
} from '../../config/businessInfo';
import {
    generateIntelligentResponse,
    generateImageAnalysisResponse
} from '../../services/aiAssistantService';
import './FloatingWidgets.css';

// Classic Blue Robot Mascot for Floating Launcher
const BlueLauncherRobotAvatar = () => (
    <svg viewBox="0 0 64 64" className="sleek-robot-svg" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
            <linearGradient id="blueRobotMascotGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#38BDF8" />
                <stop offset="50%" stopColor="#2563EB" />
                <stop offset="100%" stopColor="#1E3A8A" />
            </linearGradient>
            <linearGradient id="blueRobotScreenDark" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#0B1A3D" />
                <stop offset="100%" stopColor="#07132B" />
            </linearGradient>
            <filter id="blueAura" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="0" stdDeviation="2.5" floodColor="#38BDF8" floodOpacity="0.9" />
            </filter>
        </defs>

        {/* Head Shell */}
        <rect x="12" y="16" width="40" height="34" rx="14" fill="url(#blueRobotMascotGrad)" stroke="#BAE6FD" strokeWidth="1.5" />

        {/* Top Antenna */}
        <line x1="32" y1="16" x2="32" y2="8" stroke="#7DD3FC" strokeWidth="2.5" strokeLinecap="round" />
        <circle cx="32" cy="7" r="3.5" fill="#38BDF8" filter="url(#blueAura)" />

        {/* Ears */}
        <rect x="8" y="27" width="4" height="12" rx="2" fill="#38BDF8" />
        <rect x="52" y="27" width="4" height="12" rx="2" fill="#38BDF8" />

        {/* Visor / Face Screen */}
        <rect x="17" y="22" width="30" height="22" rx="9" fill="url(#blueRobotScreenDark)" stroke="#38BDF8" strokeWidth="1" />

        {/* Glowing Eyes */}
        <circle cx="26" cy="31" r="3.8" fill="#38BDF8" />
        <circle cx="38" cy="31" r="3.8" fill="#38BDF8" />

        {/* Highlights */}
        <circle cx="27.5" cy="29.5" r="1.3" fill="#FFFFFF" />
        <circle cx="39.5" cy="29.5" r="1.3" fill="#FFFFFF" />

        {/* Smile */}
        <path d="M28 37C29.5 39 34.5 39 36 37" stroke="#38BDF8" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

// Sleek Brand Orange / Blue Robot Mascot Icon for Inside Chat
const RiseUpRobotAvatar = () => (
    <svg viewBox="0 0 64 64" className="sleek-robot-svg" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
            <linearGradient id="robotMascotGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FB923C" />
                <stop offset="50%" stopColor="#EA580C" />
                <stop offset="100%" stopColor="#C2410C" />
            </linearGradient>
            <linearGradient id="robotScreenDark" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#0B1A3D" />
                <stop offset="100%" stopColor="#07132B" />
            </linearGradient>
            <filter id="orangeAura" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="0" stdDeviation="2.5" floodColor="#FB923C" floodOpacity="0.8" />
            </filter>
        </defs>

        {/* Head Shell */}
        <rect x="12" y="16" width="40" height="34" rx="14" fill="url(#robotMascotGrad)" stroke="#FED7AA" strokeWidth="1.5" />

        {/* Top Antenna */}
        <line x1="32" y1="16" x2="32" y2="8" stroke="#FDBA74" strokeWidth="2.5" strokeLinecap="round" />
        <circle cx="32" cy="7" r="3.5" fill="#FB923C" filter="url(#orangeAura)" />

        {/* Ears */}
        <rect x="8" y="27" width="4" height="12" rx="2" fill="#FB923C" />
        <rect x="52" y="27" width="4" height="12" rx="2" fill="#FB923C" />

        {/* Visor / Face Screen */}
        <rect x="17" y="22" width="30" height="22" rx="9" fill="url(#robotScreenDark)" stroke="#EA580C" strokeWidth="1" />

        {/* Glowing Eyes */}
        <circle cx="26" cy="31" r="3.8" fill="#38BDF8" />
        <circle cx="38" cy="31" r="3.8" fill="#38BDF8" />

        {/* Highlights */}
        <circle cx="27.5" cy="29.5" r="1.3" fill="#FFFFFF" />
        <circle cx="39.5" cy="29.5" r="1.3" fill="#FFFFFF" />

        {/* Smile */}
        <path d="M28 37C29.5 39 34.5 39 36 37" stroke="#38BDF8" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const INITIAL_GREETING = "Assalam-o-Alaikum! Welcome to Rahber Corporation. I am your 24 working hours AI shopping assistant. How may I assist you today with appliance prices, instant cash discounts, 4-12 month installment plans (4 month 0% markup), or Lahore showroom visits?";

const SUGGESTED_INQUIRIES = [
    {
        title: 'Upload Photo for Price Check',
        desc: 'Instant price, discount & monthly installment',
        icon: '📸',
        isUpload: true,
        prompt: 'Upload appliance photo to check price and installment plan'
    },
    {
        title: '1.5 Ton Inverter ACs',
        desc: 'Electricity savings, cooling & price deals',
        icon: '❄️',
        prompt: 'Which 1.5 ton Inverter AC gives maximum electricity savings in Lahore?'
    },
    {
        title: '0% Markup (4-Month Plan)',
        desc: 'Zero extra markup on listed retail MRP',
        icon: '⚡',
        prompt: 'Tell me about the 0% markup 4-month installment offer.'
    },
    {
        title: 'Refrigerators & Freezers',
        desc: 'Cooling retention models & stock availability',
        icon: '🧊',
        prompt: 'What refrigerators and deep freezers with high cooling retention are in stock?'
    },
    {
        title: 'Instant Cash Discount',
        desc: 'Lahore doorstep delivery with direct bank transfer',
        icon: '💰',
        prompt: 'How do I get the instant cash discount on MRP with Lahore showroom delivery?'
    },
    {
        title: 'Smart LED TVs (4K UHD)',
        desc: 'TCL, Samsung & Google TVs with warranty',
        icon: '📺',
        prompt: 'What are the prices for 43 and 55 inch 4K Smart TVs with warranty?'
    },
    {
        title: 'Showroom Location & Timings',
        desc: 'Main Bazar Chandni Chowk, Sanda Kalan',
        icon: '📍',
        prompt: 'What is your showroom address in Sanda Kalan Lahore and timings?'
    },
];

const FloatingWidgets = () => {
    const [isScrolledDown, setIsScrolledDown] = useState(false);
    const [isAiOpen, setIsAiOpen] = useState(false);
    const [areWidgetsHidden, setAreWidgetsHidden] = useState(false);

    // Draggable position for the AI Assistant Robot ONLY
    const [robotPos, setRobotPos] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const justTappedRef = useRef(false);
    const touchStartTimestampRef = useRef(0);

    // Image Upload State
    const [selectedImage, setSelectedImage] = useState(null);
    const fileInputRef = useRef(null);

    // Chat messages
    const [messages, setMessages] = useState([
        {
            id: 1,
            sender: 'bot',
            text: INITIAL_GREETING,
            time: 'Online'
        }
    ]);
    const [inputText, setInputText] = useState('');
    const [isTyping, setIsTyping] = useState(false);

    const chatBodyRef = useRef(null);
    const robotRef = useRef(null);

    const dragRef = useRef({
        startX: 0,
        startY: 0,
        elemStartX: 0,
        elemStartY: 0,
        moved: false,
    });

    const handleSendMessageRef = useRef(null);

    const handleImageSelect = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;

        // Check if image
        if (!file.type.startsWith('image/')) {
            alert('Please select an image file (PNG, JPG, WEBP).');
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            setSelectedImage({
                file,
                fileName: file.name,
                previewUrl: event.target.result,
            });
        };
        reader.readAsDataURL(file);
    };

    const removeSelectedImage = () => {
        setSelectedImage(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Detect scroll for bottom-left scroll-to-top button
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 60) {
                setIsScrolledDown(true);
            } else {
                setIsScrolledDown(false);
            }
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // USER REQUIREMENT: Always keep chat anchored to TOP on open and top-reading on long response
    // When a new message comes in, scroll smoothly so user can read from the beginning of the message!
    useEffect(() => {
        if (isAiOpen && chatBodyRef.current) {
            // If just opened, start at top (scrollTop = 0)
            if (messages.length === 1) {
                chatBodyRef.current.scrollTop = 0;
            }
        }
    }, [isAiOpen, messages.length]);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Drag and Tap handlers for Draggable Robot (Mouse + Touch)
    const handlePointerDown = (e) => {
        const isTouch = Boolean(e.touches && e.touches.length > 0);
        const clientX = isTouch ? e.touches[0].clientX : e.clientX;
        const clientY = isTouch ? e.touches[0].clientY : e.clientY;

        touchStartTimestampRef.current = Date.now();

        const rect = robotRef.current?.getBoundingClientRect();
        const currentLeft = rect ? rect.left : clientX - 25;
        const currentTop = rect ? rect.top : clientY - 25;

        dragRef.current = {
            startX: clientX,
            startY: clientY,
            elemStartX: currentLeft,
            elemStartY: currentTop,
            moved: false,
        };

        const onPointerMove = (moveEvt) => {
            const mX = moveEvt.touches ? moveEvt.touches[0].clientX : moveEvt.clientX;
            const mY = moveEvt.touches ? moveEvt.touches[0].clientY : moveEvt.clientY;

            const deltaX = mX - dragRef.current.startX;
            const deltaY = mY - dragRef.current.startY;

            // Use 15px threshold for touch so human tap jitter does not count as dragging
            if (Math.abs(deltaX) > 15 || Math.abs(deltaY) > 15) {
                dragRef.current.moved = true;
                setIsDragging(true);

                const newX = Math.max(10, Math.min(window.innerWidth - 60, dragRef.current.elemStartX + deltaX));
                const newY = Math.max(10, Math.min(window.innerHeight - 60, dragRef.current.elemStartY + deltaY));

                setRobotPos({ x: newX, y: newY });
            }
        };

        const onPointerUp = () => {
            window.removeEventListener('mousemove', onPointerMove);
            window.removeEventListener('mouseup', onPointerUp);
            window.removeEventListener('touchmove', onPointerMove);
            window.removeEventListener('touchend', onPointerUp);

            setIsDragging(false);

            // On mobile touch: If user tapped without dragging (< 400ms & not moved)
            const duration = Date.now() - touchStartTimestampRef.current;
            if (isTouch && !dragRef.current.moved && duration < 400) {
                justTappedRef.current = true;
                setIsAiOpen((prev) => !prev);
                setTimeout(() => {
                    justTappedRef.current = false;
                }, 400);
            }
        };

        window.addEventListener('mousemove', onPointerMove, { passive: true });
        window.addEventListener('mouseup', onPointerUp);
        window.addEventListener('touchmove', onPointerMove, { passive: true });
        window.addEventListener('touchend', onPointerUp);
    };

    const handleRobotClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        // If already handled by immediate touch-tap handler, avoid double-toggle
        if (justTappedRef.current) {
            justTappedRef.current = false;
            return;
        }
        // If it was a real drag movement, do not toggle
        if (dragRef.current.moved) {
            dragRef.current.moved = false;
            return;
        }
        setIsAiOpen((prev) => !prev);
    };

    const handleSendMessage = (customText) => {
        const text = (customText || inputText).trim();
        const currentImg = selectedImage;

        if (!text && !currentImg) return;

        const userMsg = {
            id: Date.now(),
            sender: 'user',
            text: text || (currentImg ? 'Uploaded photo for price quotation & plan check' : ''),
            imageUrl: currentImg ? currentImg.previewUrl : null,
            fileName: currentImg ? currentImg.fileName : null,
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };

        setMessages((prev) => [...prev, userMsg]);
        setInputText('');
        setSelectedImage(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        setIsTyping(true);

        // Scroll user message into view
        setTimeout(() => {
            if (chatBodyRef.current) {
                chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
            }
        }, 50);

        // Generate intelligent OpenAI-level reply with real catalog logic or image analysis
        setTimeout(() => {
            let reply = '';
            if (currentImg) {
                reply = generateImageAnalysisResponse(currentImg.fileName, text);
            } else {
                reply = generateIntelligentResponse(text, messages);
            }

            const botMsg = {
                id: Date.now() + 1,
                sender: 'bot',
                text: reply,
                time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            };
            setMessages((prev) => [...prev, botMsg]);
            setIsTyping(false);

            // Automatically position viewport so user reads the start of the message
            setTimeout(() => {
                if (chatBodyRef.current) {
                    const botElements = chatBodyRef.current.querySelectorAll('.ai-msg--bot');
                    if (botElements.length > 0) {
                        const lastBotElem = botElements[botElements.length - 1];
                        lastBotElem.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }
            }, 60);
        }, 400);
    };

    handleSendMessageRef.current = handleSendMessage;

    // If user hides floating widgets for relaxed viewing
    if (areWidgetsHidden) {
        return (
            <button
                type="button"
                className="floating-unhide-pill"
                onClick={() => setAreWidgetsHidden(false)}
                title="Show WhatsApp and AI Shopping Assistant"
                aria-label="Unhide widgets"
            >
                <FiEye /> <span>Show Help</span>
            </button>
        );
    }

    return (
        <>
            {/* ── 01: Bottom-Left Scroll-to-Top Button ── */}
            {isScrolledDown && (
                <button
                    type="button"
                    onClick={scrollToTop}
                    className="floating-scroll-up-btn"
                    aria-label="Scroll to top"
                    title="Scroll to top of page"
                >
                    <FiArrowUp className="scroll-up-icon" />
                </button>
            )}

            {/* ── 02: Bottom-Right Fixed Glowing WhatsApp Button ── */}
            <div className="floating-wa-anchor">
                <a
                    href={BUSINESS_WHATSAPP_CHAT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="floating-wa-btn"
                    aria-label="Chat with Showroom on WhatsApp"
                    title="Chat on WhatsApp with Rahber Showroom Specialists"
                >
                    <FaWhatsapp className="floating-wa-icon" />
                    <span className="wa-glow-pulse" />
                </a>
            </div>

            {/* ── 03: Floating Draggable AI Assistant Robot ── */}
            <div
                ref={robotRef}
                onClick={handleRobotClick}
                onMouseDown={handlePointerDown}
                onTouchStart={handlePointerDown}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setIsAiOpen((prev) => !prev);
                    }
                }}
                style={
                    robotPos
                        ? {
                              position: 'fixed',
                              left: `${robotPos.x}px`,
                              top: `${robotPos.y}px`,
                              zIndex: 1000,
                              touchAction: 'none',
                          }
                        : undefined
                }
                className={`floating-ai-robot-btn ${robotPos ? 'floating-ai-robot-btn--custom' : 'floating-ai-robot-btn--default'} ${
                    isAiOpen ? 'active' : ''
                } ${isDragging ? 'dragging' : ''}`}
                role="button"
                tabIndex={0}
                aria-label="Open Rahber AI Shopping Assistant (Drag anywhere)"
                title="Click to chat or drag anywhere on screen"
            >
                <div className="ai-robot-blue-glow-aura" />
                <div className="ai-robot-avatar-disc">
                    <BlueLauncherRobotAvatar />
                </div>
                <span className="ai-robot-badge-indicator" />
            </div>

            {/* ── 04: Subtle Hide Button ── */}
            <button
                type="button"
                className="floating-hide-toggle"
                onClick={() => setAreWidgetsHidden(true)}
                title="Hide floating widgets for distraction-free reading"
                aria-label="Hide floating buttons"
            >
                <FiEyeOff />
            </button>

            {/* ── 05: Interactive Premium AI Assistant Window ── */}
            {isAiOpen && (
                <>
                    {/* Dark Blurred Backdrop for Mobile & Focused Desktop */}
                    <div
                        className="ai-chat-backdrop"
                        onClick={() => {
                            setIsAiOpen(false);
                            if (window.speechSynthesis) window.speechSynthesis.cancel();
                        }}
                        aria-hidden="true"
                    />

                    <div className="ai-chat-window riseup-theme rahber-ai-modal" role="dialog" aria-label="Rahber AI Assistant">
                        {/* Premium Clean Header with Top-Right Corner Close Button */}
                        <div className="ai-chat-header riseup-header rahber-ai-header">
                            <div className="ai-header-info">
                                <div className="ai-header-avatar riseup-avatar rahber-ai-avatar">
                                    <RiseUpRobotAvatar />
                                </div>
                                <div className="ai-header-title-box">
                                    <h4 className="ai-header-title">Rahber AI Assistant</h4>
                                    <span className="ai-header-status">
                                        <span className="ai-status-dot" /> Online • 24/7 Helper
                                    </span>
                                </div>
                            </div>

                            {/* Standard Top-Right Corner Close Button (RiseUp style) */}
                            <button
                                type="button"
                                onClick={() => {
                                    setIsAiOpen(false);
                                    if (window.speechSynthesis) window.speechSynthesis.cancel();
                                }}
                                className="ai-chat-corner-close-btn"
                                aria-label="Close Assistant"
                                title="Close (Esc)"
                            >
                                <FiX />
                            </button>
                        </div>

                        {/* Messages Body (Messages start right at the top) */}
                        <div className="ai-chat-body" ref={chatBodyRef}>
                            {/* Interactive Message Thread */}
                            <div className="ai-conversation-thread">
                                {messages.map((m, idx) => (
                                    <React.Fragment key={m.id || idx}>
                                        <div className={`ai-msg ai-msg--${m.sender}`}>
                                            <div className="ai-msg-bubble">
                                                {m.imageUrl && (
                                                    <div className="ai-msg-image-wrap">
                                                        <img
                                                            src={m.imageUrl}
                                                            alt={m.fileName || 'Uploaded appliance'}
                                                            className="ai-msg-uploaded-img"
                                                        />
                                                    </div>
                                                )}
                                                {m.text && <p style={{ whiteSpace: 'pre-line' }}>{m.text}</p>}
                                                <div className="ai-msg-footer">
                                                    <span className="ai-msg-time">{m.time}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Redesigned Clean & Structured Quick Inquiries Below Initial Greeting */}
                                        {idx === 0 && messages.length <= 2 && (
                                            <div className="ai-quick-inquiries-block">
                                                <div className="ai-quick-inquiries-header">
                                                    <FiHelpCircle className="inquiries-head-icon" />
                                                    <span>Quick Inquiries & Quote:</span>
                                                </div>
                                                <div className="ai-quick-inquiries-list">
                                                    {SUGGESTED_INQUIRIES.map((inq, sIdx) => (
                                                        <button
                                                            key={sIdx}
                                                            type="button"
                                                            className={`ai-inquiry-row-btn ${inq.isUpload ? 'ai-inquiry-row-btn--upload' : ''}`}
                                                            onClick={() => {
                                                                if (inq.isUpload) {
                                                                    fileInputRef.current?.click();
                                                                } else {
                                                                    handleSendMessage(inq.prompt);
                                                                }
                                                            }}
                                                            title={inq.isUpload ? 'Select photo of appliance from your device' : inq.prompt}
                                                        >
                                                            <div className="inquiry-row-left">
                                                                <span className="inquiry-row-icon">{inq.icon}</span>
                                                                <div className="inquiry-row-text">
                                                                    <strong className="inquiry-row-title">{inq.title}</strong>
                                                                    <span className="inquiry-row-desc">{inq.desc}</span>
                                                                </div>
                                                            </div>
                                                            <span className="inquiry-row-action">
                                                                {inq.isUpload ? <FiUploadCloud className="inquiry-row-arrow upload-icon" /> : <FiChevronRight className="inquiry-row-arrow" />}
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </React.Fragment>
                                ))}

                                {isTyping && (
                                    <div className="ai-msg ai-msg--bot">
                                        <div className="ai-msg-bubble ai-typing-bubble">
                                            <span className="typing-dot" />
                                            <span className="typing-dot" />
                                            <span className="typing-dot" />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>



                        {/* Image Upload Preview Bar */}
                        {selectedImage && (
                            <div className="ai-chat-image-preview-bar">
                                <div className="preview-img-box">
                                    <img src={selectedImage.previewUrl} alt="Preview" className="preview-thumbnail" />
                                    <span className="preview-filename">{selectedImage.fileName}</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={removeSelectedImage}
                                    className="preview-remove-btn"
                                    title="Remove Image"
                                    aria-label="Remove image"
                                >
                                    <FiX />
                                </button>
                            </div>
                        )}

                        {/* Input Form with Photo Upload & Send */}
                        <form
                            className="ai-chat-footer riseup-footer"
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSendMessage();
                            }}
                        >
                            {/* Hidden File Input */}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleImageSelect}
                                style={{ display: 'none' }}
                                id="ai-chat-file-upload"
                            />

                            {/* Camera / Photo Upload Button */}
                            <button
                                type="button"
                                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                className={`ai-photo-upload-btn ${selectedImage ? 'active-upload' : ''}`}
                                title="Upload appliance photo for price check"
                                aria-label="Upload photo"
                            >
                                <FiCamera />
                            </button>

                            <input
                                type="text"
                                placeholder={selectedImage ? "Add question about this photo (optional)..." : "Ask about appliances, prices, or plans..."}
                                value={inputText}
                                onChange={(e) => setInputText(e.target.value)}
                                className="ai-chat-input"
                            />

                            {/* Send Button */}
                            <button
                                type="submit"
                                disabled={!inputText.trim() && !selectedImage}
                                className="ai-chat-send-btn riseup-send-btn"
                                aria-label="Send message"
                            >
                                <FiSend />
                            </button>
                        </form>
                    </div>
                </>
            )}
        </>
    );
};

export default FloatingWidgets;
